<template>
  <div class="mb-4">
    <!-- Small screens: a dropdown instead of forcing the pill row to
         scroll sideways — "Open to Work" alone is long enough that all
         three never comfortably fit on a narrow screen without either
         wrapping (illegible, see the pills below) or scrolling (which
         hides "Pools" off-screen with no visible hint it's there).
         v-select rather than a native <select>: a native select's popup
         is positioned entirely by the browser (opens upward if it decides
         there isn't enough room below, which this component being fairly
         far down the page made likely), and there's no CSS/JS way to
         override that. v-select renders its own menu, always anchored
         below the toggle by plain CSS (`top: calc(100% ...)`, no flip
         logic at all — confirmed by reading its source) — the same
         approach already used for the Trade filter, just non-searchable
         here since there are only 3 fixed options. -->
    <v-select
      :value="currentTab"
      :options="tabOptions"
      :reduce="(option) => option.value"
      label="text"
      :searchable="false"
      :clearable="false"
      class="candidates-tabs-select d-sm-none"
      @input="onSelectChange"
    />

    <!-- Normal screens: pill tabs, all on one line with room to spare. -->
    <b-nav pills class="candidates-tabs d-none d-sm-flex">
      <b-nav-item
        v-if="canSearchCandidates"
        :to="{ name: 'candidates.list-page' }"
        exact
      >
        Discover
      </b-nav-item>
      <b-nav-item
        v-if="canSearchCandidates"
        :to="{ name: 'candidates.in-progress-page' }"
      >
        Candidates
      </b-nav-item>
      <b-nav-item
        v-if="canSearchCandidates && canManagePools"
        :to="{ name: 'candidates.pools-page' }"
      >
        Pools
      </b-nav-item>
      <b-nav-item v-if="canViewJobs" :to="{ name: 'jobs.list-page' }">
        Job Postings
      </b-nav-item>
    </b-nav>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

// Shared by the candidates pages (ListPage, InProgressPage, PoolsPage) and the
// Job Postings pages (src/views/jobs) — the Hiring section. Rendered
// inside each page's own content flow (after its `header-image`
// nav-clearance spacer) rather than above `router-view` in index.vue —
// MainLayout only reserves fixed-navbar clearance for routed page content,
// so a tab bar placed ahead of it renders invisibly underneath the navbar
// (see global CSS gotchas).
export default {
  name: 'CandidatesTabs',

  components: {
    vSelect,
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),

    canManagePools() {
      return this.hasPermission('candidates.manage_pools')
    },

    // The strip is shared with the Job Postings pages, where an admin may
    // have jobs access but not candidate search (or the reverse).
    canSearchCandidates() {
      return (
        !!this.$env.openToWorkEnabled && this.hasPermission('candidates.view')
      )
    },

    canViewJobs() {
      return this.hasPermission('jobs.view')
    },

    // Route names double as the <select> option values — mirrors the
    // b-nav-item `:to` targets below exactly, so the dropdown and the pill
    // row can never drift into offering different destinations.
    tabOptions() {
      const options = []
      if (this.canSearchCandidates) {
        options.push(
          { value: 'candidates.list-page', text: 'Discover' },
          { value: 'candidates.in-progress-page', text: 'Candidates' },
        )
        if (this.canManagePools) {
          options.push({ value: 'candidates.pools-page', text: 'Pools' })
        }
      }
      if (this.canViewJobs) {
        options.push({ value: 'jobs.list-page', text: 'Job Postings' })
      }
      return options
    },

    // Every /jobs page (new, view, edit) sits under the Job Postings tab.
    currentTab() {
      const name = this.$route.name || ''
      return name.startsWith('jobs.') ? 'jobs.list-page' : name
    },
  },

  methods: {
    onSelectChange(routeName) {
      if (routeName !== this.$route.name) {
        this.$router.push({ name: routeName })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Bootstrap's `tabs` style leans on an underline/border to show which tab
// is active — a look that depends on the row staying on one line so the
// active tab's border visually connects down into the panel below it. This
// nav wraps well before that width (3 items, "Open to Work" is long), and
// once wrapped, an unselected tab has no border/background of its own at
// all, so it's indistinguishable from a stray line of text. Pills give every
// item its own visible shape regardless of whether the row is one line or
// two, so which item is a tab (and which one's active) stays legible either
// way — the `nowrap`/`overflow-x` below is then just a nice-to-have for
// wider phones, not what's carrying the "can you tell these apart" job.
.candidates-tabs {
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  /deep/ .nav-link {
    font-weight: 600;
    color: #6c7581;
    white-space: nowrap;
    background-color: #f4f5f7;
    border-radius: 20px;
    padding: 0.4rem 1rem;
    margin-right: 0.5rem;

    &.active {
      color: #fff;
      // $brand (#006CFF) — hardcoded rather than imported since this
      // component's scoped style has no access to the abstract/_variables
      // partial (no sass-resources-loader wired up in vue.config.js, and no
      // other component in this codebase imports it either).
      background-color: #006cff;
    }
  }
}

// Matches the app's usual .custom-select look (see src/assets/scss/layouts/
// _forms.scss and the same pattern already used for the Trade filter) since
// v-select ships its own box model rather than inheriting Bootstrap's.
.candidates-tabs-select {
  --vs-border-color: #e5e5e5;
  --vs-border-width: 2px;
  --vs-border-radius: 8px;
  --vs-font-size: 0.875rem;

  ::v-deep .vs__dropdown-toggle {
    min-height: 50px;
    align-items: center;
  }

  ::v-deep .vs__selected {
    font-size: 0.875rem;
  }
}
</style>
